// AuthzShell.tsx — authz-specific configuration of the shared common Shell.
import { type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { BsLockFill } from 'react-icons/bs'
import { Shell, type ShellNavItem } from 'common'
import { useAuth } from '../hooks/useAuth'

interface Props { children: ReactNode }

const AuthzShell = ({ children }: Props) => {
    const navigate = useNavigate()
    const { email, scopes, photoUrl } = useAuth()

    const hasAdmin = scopes.includes('authz.admin')
    const canUseSecrets = scopes.includes('authz.secrets.use') || hasAdmin

    const navItems: ShellNavItem[] = [
        { label: 'Roles', href: '/roles' },
        { label: 'Scopes', href: '/scopes' },
    ]
    if (hasAdmin) navItems.push({ label: 'Users', href: '/users' })
    if (canUseSecrets) navItems.push({ label: 'Secrets', href: '/secrets' })
    if (hasAdmin) {
        navItems.push({ label: 'SSO', href: '/sso' })
        navItems.push({ label: 'Settings', href: '/settings' })
    }

    const handleLogout = async () => {
        try {
            await fetch('/api/logout', { method: 'POST', credentials: 'include' })
        } catch { /* ignore */ }
        localStorage.removeItem('identity_token')
        navigate('/login')
    }

    // Derive the brand from the current host so the same image serves
    // authz.lab0.us, authz.aquaduct.dev, and any other deployment without
    // a per-cluster build. SSR safe-guard returns "authz" as a generic
    // fallback (the SPA never actually renders without a window though).
    const brandHost = typeof window !== 'undefined' ? window.location.hostname : 'authz'

    return (
        <Shell
            brand={<><BsLockFill size={18} /> {brandHost}</>}
            brandHref="/dashboard"
            navItems={navItems}
            userEmail={email}
            userPhotoUrl={photoUrl}
            userHref="/dashboard"
            onLogout={handleLogout}
        >
            {children}
        </Shell>
    )
}

export default AuthzShell
